<template>
  <div class="mapbox-debug-helper">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-bug</v-icon>
        Mapbox Pipeline Debug Helper
      </v-card-title>
      
      <v-card-text>
        <div class="debug-section">
          <h3>Pipeline Segments</h3>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Segment ID</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Sensors</th>
                  <th>Feature ID Valid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="segment in pipelineSegments" :key="segment.id">
                  <td><code>{{ segment.id }}</code></td>
                  <td>{{ segment.name }}</td>
                  <td>
                    <v-chip 
                      small 
                      :color="getStatusColor(segment.healthStatus)"
                      dark
                    >
                      {{ segment.healthStatus }}
                    </v-chip>
                  </td>
                  <td>{{ segment.sensors.length }}</td>
                  <td>
                    <v-icon 
                      small 
                      :color="segmentFeatureStatus[segment.id] ? 'success' : 'error'"
                    >
                      {{ segmentFeatureStatus[segment.id] ? 'mdi-check' : 'mdi-close' }}
                    </v-icon>
                  </td>
                  <td>
                    <v-btn 
                      x-small 
                      @click="testSegmentFeature(segment)"
                      color="primary"
                    >
                      Test
                    </v-btn>
                    <v-btn 
                      x-small 
                      @click="highlightSegment(segment)"
                      color="info"
                      class="ml-1"
                    >
                      Highlight
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        
        <div class="debug-section mt-4">
          <h3>Zones</h3>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Zone ID</th>
                  <th>Zone Name</th>
                  <th>Mile Marker</th>
                  <th>Status</th>
                  <th>Alerts</th>
                  <th>Sensors</th>
                  <th>Feature ID Valid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="zone in allZones" :key="zone.id">
                  <td><code>{{ zone.id }}</code></td>
                  <td>{{ zone.name }}</td>
                  <td>{{ zone.mileMarker }}</td>
                  <td>
                    <v-chip 
                      x-small 
                      :color="getStatusColor(zone.status)"
                      dark
                    >
                      {{ zone.status }}
                    </v-chip>
                  </td>
                  <td>{{ zone.alerts }}</td>
                  <td>{{ zone.sensors.length }}</td>
                  <td>
                    <v-icon 
                      small 
                      :color="zoneFeatureStatus[zone.id] ? 'success' : 'error'"
                    >
                      {{ zoneFeatureStatus[zone.id] ? 'mdi-check' : 'mdi-close' }}
                    </v-icon>
                  </td>
                  <td>
                    <v-btn 
                      x-small 
                      @click="testZoneFeature(zone)"
                      color="primary"
                    >
                      Test
                    </v-btn>
                    <v-btn 
                      x-small 
                      @click="highlightZone(zone)"
                      color="info"
                      class="ml-1"
                    >
                      Highlight
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        
        <div class="debug-section mt-4">
          <h3>Debug Actions</h3>
          <v-btn @click="validateAllFeatures" color="primary" class="mr-2">
            <v-icon left>mdi-check-all</v-icon>
            Validate All Features
          </v-btn>
          <v-btn @click="logMapSources" color="info" class="mr-2">
            <v-icon left>mdi-layers</v-icon>
            Log Map Sources
          </v-btn>
          <v-btn @click="clearAllHighlights" color="warning">
            <v-icon left>mdi-brush</v-icon>
            Clear Highlights
          </v-btn>
        </div>
        
        <div class="debug-section mt-4" v-if="debugLog.length > 0">
          <h3>Debug Log</h3>
          <v-card outlined class="debug-log">
            <v-card-text>
              <div 
                v-for="(entry, index) in debugLog" 
                :key="index"
                class="log-entry"
                :class="entry.type"
              >
                <span class="timestamp">{{ entry.timestamp }}</span>
                <span class="message">{{ entry.message }}</span>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'MapboxDebugHelper',
  props: {
    map: {
      type: Object,
      default: null
    },
    pipelineSegments: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      segmentFeatureStatus: {},
      zoneFeatureStatus: {},
      debugLog: []
    }
  },
  computed: {
    allZones() {
      const zones = []
      this.pipelineSegments.forEach(segment => {
        segment.zones.forEach(zone => {
          zones.push({ ...zone, segmentId: segment.id })
        })
      })
      return zones
    }
  },
  watch: {
    map: {
      handler(newMap) {
        if (newMap) {
          this.validateAllFeatures()
        }
      },
      immediate: true
    }
  },
  methods: {
    log(message, type = 'info') {
      const timestamp = new Date().toLocaleTimeString()
      this.debugLog.unshift({ message, type, timestamp })
      if (this.debugLog.length > 50) {
        this.debugLog.pop()
      }
      console.log(`[Debug] ${message}`)
    },
    
    getStatusColor(status) {
      const colors = {
        healthy: 'success',
        warning: 'warning',
        critical: 'error'
      }
      return colors[status] || 'grey'
    },
    
    testSegmentFeature(segment) {
      if (!this.map) {
        this.log('Map not available', 'error')
        return
      }
      
      const sourceId = `pipeline-${segment.id}`
      
      try {
        const features = this.map.querySourceFeatures(sourceId)
        const hasValidIds = features.every(f => f.id !== undefined)
        
        this.segmentFeatureStatus = {
          ...this.segmentFeatureStatus,
          [segment.id]: hasValidIds
        }
        
        this.log(`Segment ${segment.id}: ${features.length} features, IDs valid: ${hasValidIds}`, hasValidIds ? 'success' : 'error')
        
        if (features.length > 0) {
          this.log(`Feature sample: ${JSON.stringify({
            id: features[0].id,
            properties: features[0].properties,
            geometryType: features[0].geometry.type
          })}`, 'info')
        }
      } catch (error) {
        this.log(`Error testing segment ${segment.id}: ${error.message}`, 'error')
        this.segmentFeatureStatus = {
          ...this.segmentFeatureStatus,
          [segment.id]: false
        }
      }
    },
    
    testZoneFeature(zone) {
      if (!this.map) {
        this.log('Map not available', 'error')
        return
      }
      
      const sourceId = `zone-${zone.id}`
      
      try {
        const features = this.map.querySourceFeatures(sourceId)
        const hasValidIds = features.every(f => f.id !== undefined)
        
        this.zoneFeatureStatus = {
          ...this.zoneFeatureStatus,
          [zone.id]: hasValidIds
        }
        
        this.log(`Zone ${zone.id}: ${features.length} features, IDs valid: ${hasValidIds}`, hasValidIds ? 'success' : 'error')
      } catch (error) {
        this.log(`Error testing zone ${zone.id}: ${error.message}`, 'error')
        this.zoneFeatureStatus = {
          ...this.zoneFeatureStatus,
          [zone.id]: false
        }
      }
    },
    
    highlightSegment(segment) {
      if (!this.map) {
        this.log('Map not available', 'error')
        return
      }
      
      const sourceId = `pipeline-${segment.id}`
      
      try {
        this.map.setFeatureState(
          { source: sourceId, id: segment.id },
          { selected: true }
        )
        this.log(`Highlighted segment ${segment.id}`, 'success')
      } catch (error) {
        this.log(`Failed to highlight segment ${segment.id}: ${error.message}`, 'error')
      }
    },
    
    highlightZone(zone) {
      if (!this.map) {
        this.log('Map not available', 'error')
        return
      }
      
      const sourceId = `zone-${zone.id}`
      
      try {
        this.map.setFeatureState(
          { source: sourceId, id: zone.id },
          { selected: true }
        )
        this.log(`Highlighted zone ${zone.id}`, 'success')
      } catch (error) {
        this.log(`Failed to highlight zone ${zone.id}: ${error.message}`, 'error')
      }
    },
    
    validateAllFeatures() {
      if (!this.map) {
        this.log('Map not available for validation', 'error')
        return
      }
      
      this.log('Starting full feature validation...', 'info')
      
      // Test all segments
      this.pipelineSegments.forEach(segment => {
        this.testSegmentFeature(segment)
      })
      
      // Test all zones
      this.allZones.forEach(zone => {
        this.testZoneFeature(zone)
      })
      
      this.log('Feature validation complete', 'success')
    },
    
    logMapSources() {
      if (!this.map) {
        this.log('Map not available', 'error')
        return
      }
      
      const style = this.map.getStyle()
      const sources = Object.keys(style.sources)
      const layers = style.layers.map(l => ({ id: l.id, source: l.source, type: l.type }))
      
      this.log(`Map has ${sources.length} sources and ${layers.length} layers`, 'info')
      
      console.log('Map Sources:', sources)
      console.log('Map Layers:', layers)
      
      sources.forEach(sourceId => {
        if (sourceId.includes('pipeline') || sourceId.includes('zone')) {
          const features = this.map.querySourceFeatures(sourceId)
          this.log(`Source ${sourceId}: ${features.length} features`, 'info')
        }
      })
    },
    
    clearAllHighlights() {
      if (!this.map) {
        this.log('Map not available', 'error')
        return
      }
      
      this.pipelineSegments.forEach(segment => {
        try {
          this.map.setFeatureState(
            { source: `pipeline-${segment.id}`, id: segment.id },
            { selected: false, hover: false }
          )
        } catch (error) {
          this.log(`Failed to clear highlight for segment ${segment.id}: ${error.message}`, 'error')
        }
      })
      
      this.log('Cleared all highlights', 'success')
    }
  }
}
</script>

<style scoped>
.debug-section {
  margin-bottom: 16px;
}

.debug-section h3 {
  margin-bottom: 12px;
  color: #1976d2;
}

.debug-log {
  max-height: 300px;
  overflow-y: auto;
}

.log-entry {
  display: flex;
  margin-bottom: 4px;
  font-family: monospace;
  font-size: 12px;
}

.log-entry.error {
  color: #d32f2f;
}

.log-entry.success {
  color: #388e3c;
}

.log-entry.warning {
  color: #f57c00;
}

.log-entry.info {
  color: #1976d2;
}

.timestamp {
  margin-right: 8px;
  color: #666;
  min-width: 80px;
}

.message {
  flex: 1;
}
</style> 